import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowRightCircle, Save } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { PageTitle } from "@/components/StatusBadge";
import { ImageryCatalog } from "@/components/ImageryCatalog";

const YearSelect = ({ value, onChange, years, testId }) => (
  <select data-testid={testId} value={value} onChange={(e) => onChange(+e.target.value)} className="h-11 w-full rounded-md bg-slate-950 border border-slate-800 px-3 font-mono">
    {years.map((y) => <option key={y} value={y}>{y}</option>)}
  </select>
);

export default function Instellingen() {
  const qc = useQueryClient();
  const { data: s } = useQuery({ queryKey: ["settings"], queryFn: () => api.get("/settings").then((r) => r.data) });
  const { data: y } = useQuery({ queryKey: ["years"], queryFn: () => api.get("/years").then((r) => r.data.years) });
  const [form, setForm] = useState(null);
  useEffect(() => { if (s) setForm({ baseline_year: s.baseline_year, compare_year: s.compare_year }); }, [s]);
  if (!s || !y || !form) return null;

  const refresh = (d) => { qc.setQueryData(["settings"], d); qc.invalidateQueries({ queryKey: ["stats"] }); };
  const save = async () => {
    try { refresh((await api.put("/settings", form)).data); toast.success("Jaren opgeslagen"); } catch (e) { toast.error(errMsg(e)); }
  };
  const promote = async (force) => {
    try {
      refresh((await api.post("/settings/promote", { force })).data);
      toast.success("Nieuwe basis vastgesteld");
    } catch (e) {
      if (e.response?.status === 409) {
        if (window.confirm(`${errMsg(e)}. Toch doorzetten?`)) return promote(true);
        return;
      }
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <PageTitle eyebrow="Jaarlijkse cyclus" title="Basisjaar beheer" />
      <div className="grid md:grid-cols-2 gap-6">
        <div className="border border-slate-800 bg-[#111827]/80 rounded-lg p-6 space-y-4">
          <h3 className="font-display font-semibold text-lg">Vergelijking</h3>
          <label className="block text-xs text-slate-400 space-y-1.5"><span>Basisjaar (goedgekeurde situatie)</span>
            <YearSelect testId="baseline-year-select" value={form.baseline_year} years={y} onChange={(v) => setForm({ ...form, baseline_year: v })} />
          </label>
          <label className="block text-xs text-slate-400 space-y-1.5"><span>Nieuwe luchtfoto</span>
            <YearSelect testId="compare-year-select" value={form.compare_year} years={y} onChange={(v) => setForm({ ...form, compare_year: v })} />
          </label>
          <button data-testid="save-years-btn" onClick={save} className="flex items-center gap-2 h-10 px-4 rounded-md border border-slate-700 hover:border-blue-500 text-sm"><Save className="h-4 w-4" /> Opslaan</button>
        </div>
        <div className="border border-blue-600/30 bg-blue-600/5 rounded-lg p-6 space-y-4">
          <h3 className="font-display font-semibold text-lg">Nieuwe basis vaststellen</h3>
          <p className="text-sm text-slate-400 leading-relaxed">
            Nadat alle meldingen voor <span className="font-mono text-white">{s.compare_year}</span> zijn beoordeeld, wordt deze luchtfoto de nieuwe basis.
            Daarna wordt bij de volgende jaargang opnieuw op wijzigingen gecontroleerd.
          </p>
          {s.compare_year >= Math.max(...y) ? (
            <p className="text-xs text-amber-300 border border-amber-500/30 bg-amber-500/10 rounded-md p-3" data-testid="promote-waiting">
              {s.compare_year} is de nieuwste luchtfoto. Vaststellen als basis kan zodra de jaargang {s.compare_year + 1} bij PDOK verschijnt.
            </p>
          ) : null}
          <button data-testid="promote-baseline-btn" disabled={s.compare_year >= Math.max(...y)} onClick={() => promote(false)} className="flex items-center gap-2 h-11 px-5 rounded-md bg-blue-600 hover:bg-blue-500 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed">
            <ArrowRightCircle className="h-4 w-4" /> {s.compare_year} als nieuwe basis
          </button>
        </div>
      </div>
      <ImageryCatalog settings={s} />
      <div className="border border-slate-800 bg-[#111827]/80 rounded-lg p-6 mt-6">
        <h3 className="font-display font-semibold mb-3">Historie basisjaren</h3>
        {(s.history || []).length === 0 && <p className="text-xs text-slate-500">Nog geen wijzigingen.</p>}
        {[...(s.history || [])].reverse().map((h, i) => (
          <div key={i} className="font-mono text-xs text-slate-400 py-1.5 border-b border-slate-800/60 last:border-0" data-testid={`baseline-history-${i}`}>
            {h.from} → <span className="text-white">{h.to}</span> · {new Date(h.at).toLocaleString("nl-NL")} · {h.by}
          </div>
        ))}
      </div>
    </div>
  );
}
